import { SHOPPER_CAMPAIGNS_PATH } from 'components/shopper/constants';

export interface FlowCampaignViewTarget {
  guid: string;
  flowCollectionId?: number | null;
  isHidden?: boolean;
  isAdmin?: boolean;
  /**
   * Whether the Shopper Campaigns surface (and therefore the
   * `${SHOPPER_CAMPAIGNS_PATH}/:campaignId` route) is available to this user
   * — pass `useShowSdpocCampaigns()`. Without it a hidden flow that outlived
   * the labs flag would link merchants to a route that never resolves.
   */
  hasCampaignsSurface: boolean;
}

export interface FlowCampaignView {
  path: string;
  /** True when path points at the Shopper Campaigns detail page. */
  isCampaignView: boolean;
}

/**
 * Client route for a flow-backed campaign reference.
 *
 * Hidden collections are Shopper Campaign send flows: they have no merchant
 * Flow Builder surface, so merchants are routed to the campaign's canonical
 * detail URL (keyed by the numeric flow collection id — the same URL the
 * campaigns overview links). The Flow Builder view is kept for Postscript
 * admins (debugging), when the Shopper Campaigns surface isn't available to
 * this user, or when the collection id is missing from a stale API response
 * — a working flow link beats a broken campaign one. Derive any "Campaign vs
 * Flow" labeling from `isCampaignView` so the label can never disagree with
 * the destination.
 */
export const resolveFlowCampaignView = ({
  guid,
  flowCollectionId,
  isHidden,
  isAdmin,
  hasCampaignsSurface,
}: FlowCampaignViewTarget): FlowCampaignView => {
  if (isHidden && !isAdmin && hasCampaignsSurface && flowCollectionId != null) {
    return {
      path: `${SHOPPER_CAMPAIGNS_PATH}/${flowCollectionId}`,
      isCampaignView: true,
    };
  }
  return { path: `/flows/${guid}`, isCampaignView: false };
};

export const getFlowCampaignViewPath = (
  target: FlowCampaignViewTarget,
): string => resolveFlowCampaignView(target).path;
